import { AppFunctionComponent } from "../../../../../types"
import React, { useState, MouseEvent } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { PrimaryHeading, SecondaryHeading } from "../../../heading.component"
import {
  BasicButtonLink,
  TextLink,
} from "../../../../../ui/components/link/link.component"
import {
  ContactInfoFields,
  QuestionFields,
} from "./innovation-lab-form-fields.component"
import {
  InnovationLabForm,
  InnovationLabProps,
} from "./innovation-lab.interface"
import {
  ContactInfoWrapper,
  FormBody,
  FormWrapper,
  FullSectionWrapper,
  Form,
  Error,
} from "./innovation-lab.styled"
import { BasicFullButton } from "../../../../../ui/components/button/button.component"
import useTranslationHook from "../../../../../hooks/use-translation.hook"

const initState = {
  firstAndLastName: "",
  email: "",
  phoneNumber: "",
}

const InnovationLabFormUI: AppFunctionComponent<InnovationLabForm> = ({
  config,
  submitForm,
}) => {
  const [active, setActive] = useState(false)
  const [error, setError] = useState(false)
  const { translate } = useTranslationHook()

  const showMore = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    setActive(!active)
  }

  const fieldsInitState = Object.assign(
    {},
    ...(config.questionFields || []).map((field) =>
      field.inputs
        ? Object.assign(
            {},
            ...field.inputs.map((input) => ({
              [`${field.label} - ${input.sublabel}`]: "",
            }))
          )
        : { [field.label]: "" }
    )
  )

  const methods = useForm({
    criteriaMode: "all",
    defaultValues: { ...fieldsInitState, ...initState },
  })

  const { handleSubmit, watch, formState } = methods
  watch()

  const onSubmit: SubmitHandler<InnovationLabProps> = async (data) => {
    setError(false)
    try {
      await submitForm(data)
    } catch (error) {
      setError(true)
    }
  }

  return (
    <FullSectionWrapper>
      <FormWrapper>
        <PrimaryHeading renderAs="h2">{config.title}</PrimaryHeading>
        <p>{config.subtitle}</p>
        <TextLink href={config.downloadLinkUrl} download>
          {config.downloadLinkText}
        </TextLink>
        <FormProvider {...methods}>
          <FormBody active={active}>
            <Form
              data-testid="form"
              data-netlify="true"
              name="innovation-lab-form"
              netlify-honeypot="bot-field"
              method="POST"
              onSubmit={handleSubmit(onSubmit)}
            >
              {/* Hidden input is required in order to make Netlify catch the value. */}
              <input
                type="hidden"
                name="form-name"
                value="innovation-lab-form"
              />
              {config.questionFields && <QuestionFields config={config} />}
              <ContactInfoWrapper>
                <SecondaryHeading renderAs="h2">
                  {config.contactTitle}
                </SecondaryHeading>
                <ContactInfoFields config={config} />
              </ContactInfoWrapper>
              <BasicFullButton
                type="submit"
                pending={formState.isSubmitting}
                data-testid="submit-button"
              >
                {config.buttonText}
              </BasicFullButton>
              {error && <Error>{translate("innovation-error-label")}</Error>}
            </Form>
          </FormBody>
        </FormProvider>
        {!active && (
          <BasicButtonLink onClick={showMore}>
            {translate("innovation-complete-online-button")}
          </BasicButtonLink>
        )}
      </FormWrapper>
    </FullSectionWrapper>
  )
}

export default InnovationLabFormUI
